import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { useAuth } from '../../contexts/AuthContext';
import { useUserEntitlements } from '../../hooks/useUserEntitlements';
import { Crown, Zap, Package, Users, FileText, Building2, Calendar, CreditCard, ArrowRight, TrendingUp } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const MySubscription = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { entitlements, getLimit, loading: entitlementsLoading } = useUserEntitlements();
  const [myAddOns, setMyAddOns] = useState([]);
  const [loadingAddOns, setLoadingAddOns] = useState(true);

  useEffect(() => {
    fetchMyAddOns();
  }, []);

  const fetchMyAddOns = async () => {
    try {
      const response = await axios.get(`${API}/api/user-add-ons`);
      setMyAddOns(response.data || []);
    } catch (error) {
      console.error('Error fetching add-ons:', error);
    } finally {
      setLoadingAddOns(false);
    }
  };

  const tierIcons = {
    'starter': <Zap className="h-5 w-5" />,
    'professional': <TrendingUp className="h-5 w-5" />,
    'business': <Building2 className="h-5 w-5" />,
    'enterprise': <Crown className="h-5 w-5" />
  };

  const tierColors = {
    'starter': 'bg-blue-100 text-blue-800 border-blue-300',
    'professional': 'bg-purple-100 text-purple-800 border-purple-300',
    'business': 'bg-green-100 text-green-800 border-green-300',
    'enterprise': 'bg-yellow-100 text-yellow-800 border-yellow-300'
  };

  const getTierColor = (tier) => tierColors[tier?.toLowerCase()] || 'bg-gray-100 text-gray-800 border-gray-300';

  const calculateUsagePercentage = (current, total) => {
    if (total === -1) return 0; // Unlimited
    if (total === 0) return 100; // No access
    return Math.min((current / total) * 100, 100);
  };

  const getUsageColor = (percentage) => {
    if (percentage >= 90) return 'bg-red-500';
    if (percentage >= 75) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  const limitCards = [
    { key: 'business_plans', label: 'Business Plans', icon: FileText },
    { key: 'scenarios', label: 'Scenarios', icon: TrendingUp },
    { key: 'subsidiaries', label: 'Subsidiaries', icon: Building2 },
    { key: 'users', label: 'Team Members', icon: Users }
  ];

  if (entitlementsLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">My Subscription</h1>
            <p className="text-slate-600 mt-1">Manage your plan, limits, and add-ons</p>
          </div>
          <Button
            onClick={() => navigate('/pricing')}
            className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
          >
            <Crown className="h-4 w-4 mr-2" />
            Upgrade Plan
          </Button>
        </div>

        {/* Current Plan Card */}
        <Card className="border-2">
          <CardHeader className="bg-gradient-to-r from-blue-50 to-purple-50">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`p-3 rounded-full ${getTierColor(entitlements?.tier)}`}>
                  {tierIcons[entitlements?.tier?.toLowerCase()] || <Package className="h-5 w-5" />}
                </div>
                <div>
                  <CardTitle className="text-2xl">
                    {entitlements?.tier ? entitlements.tier.charAt(0).toUpperCase() + entitlements.tier.slice(1) : 'Free'} Plan
                  </CardTitle>
                  <CardDescription>Your current subscription tier</CardDescription>
                </div>
              </div>
              <Badge variant="outline" className={`text-lg px-4 py-2 ${getTierColor(entitlements?.tier)}`}>
                Active
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="flex items-center gap-3">
                <Calendar className="h-5 w-5 text-slate-500" />
                <div>
                  <p className="text-sm text-slate-600">Billing Cycle</p>
                  <p className="font-semibold">Monthly</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <CreditCard className="h-5 w-5 text-slate-500" />
                <div>
                  <p className="text-sm text-slate-600">Next Billing</p>
                  <p className="font-semibold">Auto-renew</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Package className="h-5 w-5 text-slate-500" />
                <div>
                  <p className="text-sm text-slate-600">Active Add-ons</p>
                  <p className="font-semibold">{myAddOns.length} add-ons</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Usage & Limits */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              Usage & Limits
            </CardTitle>
            <CardDescription>Track your resource usage across your plan</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {limitCards.map(({ key, label, icon: Icon }) => {
                const limit = getLimit(key);
                const percentage = calculateUsagePercentage(limit?.current || 0, limit?.total || 0);
                
                return (
                  <div key={key} className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Icon className="h-4 w-4 text-slate-600" />
                        <span className="font-medium text-slate-700">{label}</span>
                      </div>
                      <span className="text-sm font-bold text-slate-900">
                        {limit?.current || 0} / {limit?.total === -1 ? '∞' : limit?.total || 0}
                      </span>
                    </div>
                    <Progress 
                      value={percentage} 
                      className="h-2"
                      indicatorClassName={getUsageColor(percentage)}
                    />
                    {percentage >= 80 && limit?.total !== -1 && (
                      <p className="text-xs text-amber-600 font-medium">
                        ⚠️ Close to limit - consider upgrading
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Active Add-ons */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Package className="h-5 w-5" />
                  Active Add-ons
                </CardTitle>
                <CardDescription>Enhance your subscription with add-ons</CardDescription>
              </div>
              <Button variant="outline" onClick={() => navigate('/add-ons')}>
                Browse Marketplace
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {loadingAddOns ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              </div>
            ) : myAddOns.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {myAddOns.map((addon) => (
                  <Card key={addon.id} className="border-2">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-lg">{addon.name}</CardTitle>
                      <CardDescription className="text-xs">{addon.category}</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-slate-600">Status:</span>
                          <Badge variant="success">Active</Badge>
                        </div>
                        {addon.expires_at && (
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-slate-600">Expires:</span>
                            <span className="font-medium">{new Date(addon.expires_at).toLocaleDateString()}</span>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <Package className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-600 mb-4">No active add-ons yet</p>
                <Button onClick={() => navigate('/add-ons')}>
                  Explore Add-ons
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Upgrade Recommendation */}
        {entitlements?.tier?.toLowerCase() === 'starter' && (
          <Card className="border-2 border-blue-300 bg-gradient-to-br from-blue-50 to-purple-50">
            <CardContent className="pt-6">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-blue-100 rounded-full">
                  <TrendingUp className="h-6 w-6 text-blue-600" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-slate-900 mb-1">Unlock More Power</h3>
                  <p className="text-slate-700 mb-4">
                    Upgrade to Professional for 5 business plans, unlimited scenarios, and advanced features.
                  </p>
                  <Button onClick={() => navigate('/pricing')} className="bg-blue-600 hover:bg-blue-700">
                    <Crown className="h-4 w-4 mr-2" />
                    View Upgrade Options
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default MySubscription;
